import {Box, Text} from "@nike/eds";
import {edsSpace16} from "@nike/eds/dist/tokens";
import {DashboardTable} from "./DashboardTable";
import {useContext} from "react";
import SnackbarContext from "../../providers/snackbar-context";
import {
    UnitsType,
    InsufficientInventoryCreated,
    ManualCorrections,
    UnexpectedReceipts,
    DutiesSaved,
    DutiesLiabilityIndication,
    AlternativeDeterminationEntries,
    AmountOfFallbackEntriesPerTrigger, DutiesSavedNetting
} from '../../graphql/audit-overview-api/api';
import useSWR from "swr";

export function Dashboard({inventoryService}) {
    const snackbarCtx = useContext(SnackbarContext);

    const unitsConfig = [
        {header: "YearMonth", accessor: "yearMonth"},
        {header: "Customs Status", accessor: "actualCustomsStatus"},
        {header: "Triggered by", accessor: "trigger"},
        {header: "Number of Units", accessor: "totalAmount", formatNumber: true}
    ];

    const insufficientInventoryConfig = [
        {header: "YearMonth", accessor: "yearMonth"},
        {header: "Triggered by", accessor: "triggeredBy"},
        {header: "Customs Status", accessor: "customsStatus"},
        {header: "Number of Units", accessor: "numberOfUnits", formatNumber: true}
    ];

    const manualCorrectionsConfig = [
        {header: "YearMonth", accessor: "yearMonth"},
        {header: "Action", accessor: "action"},
        {header: "Customs Status", accessor: "customsStatus"},
        {header: "Number of Units", accessor: "numberOfUnits", formatNumber: true}
    ];

    const unexpectedReceiptsConfig = [
        {header: "YearMonth", accessor: "yearMonth"},
        {header: "Action", accessor: "action"},
        {header: "Customs Status", accessor: "customsStatus"},
        {header: "Number of Units", accessor: "numberOfUnits", formatNumber: true}
    ];

    const dutiesSavedConfig = [
        {header: "YearMonth", accessor: "yearMonth"},
        {header: "Duties Saved (€)", accessor: "dutiesSaved", formatCurrency: true},
        {header: "Duties Used (€)", accessor: "dutiesUsed", formatCurrency: true}
    ];

    const dutiesSavedNettingConfig = [
        {header: "YearMonth", accessor: "yearMonth"},
        {header: "Duties Saved (€)", accessor: "dutiesSaved", formatCurrency: true},
        {header: "Duties Used (€)", accessor: "dutiesUsed", formatCurrency: true}
    ];

    const dutiesLiabilityIndicationConfig = [
        {header: "Location", accessor: "location"},
        {header: "Duty Liability Indication (€)", accessor: "dutyLiabilityIndication", formatCurrency: true},
        {header: "Duties Used (€)", accessor: "dutiesUsed", formatCurrency: true}
    ];
    const alternativeDeterminationEntriesConfig = [
        {header: "YearMonth", accessor: "yearMonth"},
        {header: "Alternative Valuation Method", accessor: "alternativeValuationMethod"},
        {header: "Number of Entries", accessor: "numberOfEntries", formatNumber: true},
        {header: "Number of Units", accessor: "numberOfUnits", formatNumber: true}
    ];
    const amountOfFallbackEntriesPerTriggerConfig = [
        {header: "YearMonth", accessor: "yearMonth"},
        {header: "Trigger", accessor: "trigger"},
        {header: "Customs Status", accessor: "customsStatus"},
        {header: "Number of Entries", accessor: "numberOfEntries", formatNumber: true},
        {header: "Number of Units", accessor: "numberOfUnits", formatNumber: true}
    ];

    const titleStyle = {
        marginBottom: edsSpace16
    }

    const fetcher = async () => {
        return await inventoryService.getDashboard()
            .catch(error => {
                snackbarCtx.displayMsg(error.message, "error");
            });
    }

    const {data, isLoading} = useSWR("dashboard", fetcher, {
        refreshInterval: 60000
    });

    let unitsCreated: UnitsType[] | undefined = data?.data.getDashboard.unitsCreated;
    let unitsRemoved: UnitsType[] | undefined = data?.data.getDashboard.unitsRemoved;
    let insufficientInventory: InsufficientInventoryCreated[] | undefined = data?.data.getDashboard.insufficientInventoryCreated;
    let manualCorrections: ManualCorrections[] | undefined = data?.data.getDashboard.manualCorrections;
    let unexpectedReceipts: UnexpectedReceipts[] | undefined = data?.data.getDashboard.unexpectedReceipts;
    let dutiesSaved: DutiesSaved[] | undefined = data?.data.getDashboard.dutiesSaved;
    let dutiesSavedNetting: DutiesSavedNetting[] | undefined = data?.data.getDashboard.dutiesSavedNetting;
    let dutiesLiabilityIndication: DutiesLiabilityIndication[] | undefined = data?.data.getDashboard.dutiesLiabilityIndication;
    let alternativeDeterminationEntries: AlternativeDeterminationEntries[] | undefined = data?.data.getDashboard.alternativeDeterminationEntries;
    let amountOfFallbackEntriesPerTrigger: AmountOfFallbackEntriesPerTrigger[] | undefined = data?.data.getDashboard.amountOfFallbackEntriesPerTrigger;

    return (
        <div>
            <Text style={titleStyle} font={"display-3"} as={"h3"}>DASHBOARD</Text>

            <div className="eds-grid eds-grid--m-cols-6">
                <Box className="eds-grid--m-col-6">
                    <Text style={titleStyle} font={"title-3"} as={"h5"}>Units entered & removed</Text>
                </Box>
                <Box className="eds-grid--m-col-3">
                    <DashboardTable isLoading={isLoading} data={unitsCreated} tableTitle={"Number of units entered"}
                                    columnConfig={unitsConfig}/>
                </Box>
                <Box className="eds-grid--m-col-3">
                    <DashboardTable isLoading={isLoading} data={unitsRemoved} tableTitle={"Number of units removed"}
                                    columnConfig={unitsConfig}/>
                </Box>
                <Box className="eds-grid--m-col-6">
                    <Text style={titleStyle} font={"title-3"} as={"h5"}>KPIs</Text>
                </Box>
                <Box className="eds-grid--s-col-3">
                    <DashboardTable isLoading={isLoading} data={insufficientInventory}
                                    tableTitle={"Insufficient Inventory Created"}
                                    columnConfig={insufficientInventoryConfig}/>
                </Box>
                <Box className="eds-grid--s-col-3">
                    <DashboardTable isLoading={isLoading} data={manualCorrections} tableTitle={"Manual Corrections"}
                                    columnConfig={manualCorrectionsConfig}/>
                </Box>
                <Box className="eds-grid--s-col-3">
                    <DashboardTable isLoading={isLoading} data={unexpectedReceipts} tableTitle={"Unexpected Receipts"}
                                    columnConfig={unexpectedReceiptsConfig}/>
                </Box>
                <Box className="eds-grid--m-col-6">
                    <Text style={titleStyle} font={"title-3"} as={"h5"}>Alternative valuation</Text>
                </Box>
                <Box className="eds-grid--m-col-3">
                    <DashboardTable isLoading={isLoading} data={alternativeDeterminationEntries}
                                    tableTitle={"Amount of entries with alternative determination method"}
                                    columnConfig={alternativeDeterminationEntriesConfig}/>
                </Box>
                <Box className="eds-grid--m-col-3">
                    <DashboardTable isLoading={isLoading} data={amountOfFallbackEntriesPerTrigger}
                                    tableTitle={"Amount of fallback entries per trigger"}
                                    columnConfig={amountOfFallbackEntriesPerTriggerConfig}/>
                </Box>
                <Box className="eds-grid--m-col-6">
                    <Text style={titleStyle} font={"title-3"} as={"h5"}>Duties</Text>
                </Box>
                <Box className="eds-grid--m-col-3">
                    <DashboardTable isLoading={isLoading} data={dutiesSaved}
                                    tableTitle={"Duties saved (bonded goods reexported)"}
                                    columnConfig={dutiesSavedConfig}/>
                </Box>
                <Box className="eds-grid--m-col-3">
                    <DashboardTable isLoading={isLoading} data={dutiesSavedNetting}
                                    tableTitle={"Duties saved (netted goods)"} columnConfig={dutiesSavedNettingConfig}/>
                </Box>
                <Box className="eds-grid--m-col-3">
                    <DashboardTable isLoading={isLoading} data={dutiesLiabilityIndication}
                                    tableTitle={"Duty liability indication (bonded goods not paid yet)"}
                                    columnConfig={dutiesLiabilityIndicationConfig}/>
                </Box>
            </div>
        </div>
    )
}